export interface BotAreaDef {
  id: string;
  label: string;
  color: string;
}

const DEFAULT_AREAS: BotAreaDef[] = [
  { id: 'fiscal-sn', label: 'Simples Nacional', color: 'green' },
  { id: 'fiscal-lp', label: 'Lucro Presumido', color: 'blue' },
  { id: 'fiscal-lr', label: 'Lucro Real', color: 'purple' },
  { id: 'fiscal-iss', label: 'ISS Municipal', color: 'teal' },
  { id: 'fiscal-efd', label: 'EFD/SPED', color: 'cyan' },
  { id: 'dp', label: 'Dept. Pessoal', color: 'orange' },
  { id: 'contabil', label: 'Contabil', color: 'yellow' },
  { id: 'integracao', label: 'Integracao', color: 'gray' },
  { id: 'admin', label: 'Administrativo', color: 'pink' },
];

const AREAS_STORAGE_KEY = 'bot-areas-list';

const loadAreasFromStorage = (): BotAreaDef[] => {
  try {
    const raw = localStorage.getItem(AREAS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveAreasToStorage = (areas: BotAreaDef[]) => {
  localStorage.setItem(AREAS_STORAGE_KEY, JSON.stringify(areas));
};

// Merge: defaults + custom (custom pode sobrescrever defaults pelo id)
const customAreasList = loadAreasFromStorage();
const mergedAreasMap = new Map<string, BotAreaDef>();
DEFAULT_AREAS.forEach(a => mergedAreasMap.set(a.id, a));
customAreasList.forEach(a => mergedAreasMap.set(a.id, a));

export const BOT_AREAS: BotAreaDef[] = Array.from(mergedAreasMap.values());

export const BOT_AREAS_MAP: Record<string, BotAreaDef> = {};
BOT_AREAS.forEach(a => {
  BOT_AREAS_MAP[a.id] = a;
});

// CRUD de areas
export const addArea = (area: BotAreaDef) => {
  mergedAreasMap.set(area.id, area);
  BOT_AREAS.length = 0;
  BOT_AREAS.push(...Array.from(mergedAreasMap.values()));
  BOT_AREAS_MAP[area.id] = area;
  saveAreasToStorage(BOT_AREAS);
};

export const updateArea = (id: string, updates: Partial<BotAreaDef>) => {
  const existing = mergedAreasMap.get(id);
  if (existing) {
    const updated = { ...existing, ...updates };
    mergedAreasMap.set(id, updated);
    BOT_AREAS.length = 0;
    BOT_AREAS.push(...Array.from(mergedAreasMap.values()));
    BOT_AREAS_MAP[id] = updated;
    saveAreasToStorage(BOT_AREAS);
  }
};

export const deleteArea = (id: string) => {
  mergedAreasMap.delete(id);
  delete BOT_AREAS_MAP[id];
  BOT_AREAS.length = 0;
  BOT_AREAS.push(...Array.from(mergedAreasMap.values()));
  // Remover bots associados a essa area
  Object.keys(BOT_AREA_MAP).forEach(bot => {
    if (BOT_AREA_MAP[bot] === id) delete BOT_AREA_MAP[bot];
  });
  saveAreasToStorage(BOT_AREAS);
};

export const AREA_COLORS: string[] = [
  'green',
  'blue',
  'purple',
  'teal',
  'cyan',
  'orange',
  'yellow',
  'gray',
  'pink',
  'red',
];

// Helper: retorna props pra Badge/Box baseado na cor (suporta chakra colors e hex custom)
// Custom: fundo tinted (20% opacidade) + texto na cor original — igual ao Chakra colorScheme
export const getAreaColorProps = (color: string) => {
  if (color.startsWith('custom-')) {
    const hex = `#${color.replace('custom-', '')}`;
    // Fundo com 18% opacidade, texto escurecido pra contraste
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const bgColor = `rgba(${r}, ${g}, ${b}, 0.16)`;
    const textColor = `rgb(${Math.max(0, r - 40)}, ${Math.max(0, g - 40)}, ${Math.max(0, b - 40)})`;
    return { bg: bgColor, color: textColor, colorScheme: undefined as any };
  }
  return { colorScheme: color, bg: undefined, color: undefined };
};

export const getAreaBg = (color: string, shade = '400') => {
  if (color.startsWith('custom-')) return `#${color.replace('custom-', '')}`;
  return `${color}.${shade}`;
};

export const TIPO_BOT_COLORS: Record<string, string> = {
  RPA: 'purple',
  ETL: 'green',
  WEBSCRAP: 'orange',
};

export const BOT_AREA_MAP: Record<string, string> = {
  // Fiscal - Simples Nacional
  BOT_ENVIO_SIMPLES_NACIONAL: 'fiscal-sn',
  TRANSMISSAO_DCTFWEB_SIMPLES_NACIONAL: 'fiscal-sn',
  // Fiscal - Lucro Presumido
  BOT_APURACAO_LUCRO_PRESUMIDO: 'fiscal-lp',
  WS_ECAC_IRPJ_CSLL: 'fiscal-lp',
  WS_ECAC_IRRF_CSRF: 'fiscal-lp',
  BOT_APURACAO_FISCAL: 'fiscal-lp',
  IND_APURACAO: 'fiscal-lp',
  BOT_EMITIR_GUIA: 'fiscal-lp',
  BOT_EMITIR_GUIA_PIS_COFINS: 'fiscal-lp',
  BOT_BAIXAR_EXTRATO: 'fiscal-lp',
  WS_BAIXAR_EXTRATO: 'fiscal-lp',
  IND_GUIAS: 'fiscal-lp',
  WS_EMITIR_GUIA_PIS_COFINS: 'fiscal-lp',
  BUSCA_DE_PARCELAMENTOS_GOB: 'fiscal-lp',
  COPIA_BOT_BAIXAR_EXTRATO: 'fiscal-lp',
  // Fiscal - EFD/SPED
  GERACAO_EFD_CONTRIBUICOES: 'fiscal-efd',
  TRANSMISSAO_EFD_CONTRIBUICOES: 'fiscal-efd',
  BOT_EFD_REINF: 'fiscal-efd',
  ETL_ERROS_EFD: 'fiscal-efd',
  BOT_TRANSMISSAO_DOMINIO: 'fiscal-efd',
  BOT_TRANSMISSAO_MIT_DCTFWEB: 'fiscal-efd',
  // Fiscal - ISS
  RELATORIO_GOIANIA_PRESTADO: 'fiscal-iss',
  RELATORIO_GOIANIA_TOMADO: 'fiscal-iss',
  ETL_RELATORIO_SP: 'fiscal-iss',
  // Departamento Pessoal
  IND_FUNCIONARIOS: 'dp',
  IND_DP_SQL: 'dp',
  IND_GUIAS_DP: 'dp',
  BOT_CALCULO_FERIAS: 'dp',
  BOT_CALCULO_RESCISAO: 'dp',
  ESPECIALISTAS_AVISO_FERIAS2: 'dp',
  ESPECIALISTAS_AVISO_RESCISAO: 'dp',
  BOT_PEDIDO_ADMISSAO: 'dp',
  AUDITORIA_REALATORIO_DCTFWEB: 'dp',
  // Contabil
  IND_HOMOLOGACAO_ERROS: 'contabil',
  IND_NOTAS_AUDITOR: 'contabil',
  IND_NOTAS_VALIDACAO: 'contabil',
  // Integracao/Data
  IND_EMPRESAS: 'integracao',
  IND_FATURAMENTO: 'integracao',
  IND_TOTAL_NOTAS: 'integracao',
  IND_TOTAL_DIARIO: 'integracao',
  IND_SIEG: 'integracao',
  IND_NOTAS_DUPLICADAS: 'integracao',
  IND_NOTAS_MANUAL: 'integracao',
  IND_PLANILHAS: 'integracao',
  IND_QUEBRAS: 'integracao',
  IND_RELATORIO_DOMINIO: 'integracao',
  ETL_AUTMAIS_NOTAS: 'integracao',
  'ETL-INTEGRA-CONTADOR': 'integracao',
  NFSE_XML_EXTRACT: 'integracao',
  SCRIPT_ENTRADAS_VALOR_QNT: 'integracao',
  SCRIPT_QT_NOTAS_UNECONT: 'integracao',
  CNPJ_GOV: 'integracao',
  BOT_NOTAS_XML_ZIG: 'integracao',
  BOT_PREPATORIO_ZIG: 'integracao',
  BOT_UNECONT: 'integracao',
  GOB_ATIVA_EMP: 'integracao',
  GOB_INATIVA_EMP: 'integracao',
  HUBCOUNT_ATIVA_EMP: 'integracao',
  SIEG_EXCLUI_EMP: 'integracao',
  UNE_EXCLUI_EMP: 'integracao',
  LISTA_ATIVAS_ACESSORIAS_DETALHADO_ATIVA: 'integracao',
  lista_ativas_acessorias_detalhado: 'integracao',
  planilha_acessorias_indicadores: 'integracao',
  // Administrativo
  BOT_SOLICITACOES_ACESSORIAS: 'admin',
  BOT_ENTREGAS_ACESSORIAS: 'admin',
  BOT_SOLICITACOES_GERAIS: 'admin',
  BOT_RELATORIO_ACESSORIAS: 'admin',
  BOT_EXCLUSAO_NFSE: 'admin',
  WS_UNE_QUESTS: 'admin',
};

// Carregar customizacoes do localStorage
const STORAGE_KEY = 'bot-areas-custom';
const loadCustomAreas = (): Record<string, string> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};
const customAreas = loadCustomAreas();
// Merge: custom sobrescreve hardcoded
Object.entries(customAreas).forEach(([bot, areaId]) => {
  BOT_AREA_MAP[bot] = areaId;
});

export const getBotArea = (botName: string): BotAreaDef | null => {
  const areaId = BOT_AREA_MAP[botName];
  if (!areaId) return null;
  return BOT_AREAS_MAP[areaId] || null;
};

export const setBotArea = (botName: string, areaId: string | null) => {
  if (areaId) {
    BOT_AREA_MAP[botName] = areaId;
    customAreas[botName] = areaId;
  } else {
    delete BOT_AREA_MAP[botName];
    delete customAreas[botName];
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(customAreas));
};

export const getBotAreaId = (botName: string): string => {
  return BOT_AREA_MAP[botName] || '';
};
